import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { updateProfile } from '../store/slices/authSlice'
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  KeyIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon
} from '@heroicons/react/24/outline'

const ProfileSettings = () => {
  const dispatch = useAppDispatch()
  const { user, loading } = useAppSelector(state => state.auth)
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile')
  const [editingField, setEditingField] = useState<string | null>(null)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [originalValues, setOriginalValues] = useState({ name: '', email: '' })

  useEffect(() => {
    if (user) {
      const newData = {
        name: user.name,
        email: user.email
      }
      setFormData(prev => ({
        ...prev,
        ...newData
      }))
      setOriginalValues(newData)
    }
  }, [user])


  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: '新密碼與確認密碼不一致' })
      return
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: '新密碼至少需要 6 個字符' })
      return
    }

    setUpdateLoading(true)
    setMessage(null)

    try {
      await dispatch(updateProfile({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      })).unwrap()
      
      setMessage({ type: 'success', text: '密碼更新成功！' })
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }))
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '密碼更新失敗，請檢查當前密碼是否正確' })
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleFieldClick = (fieldName: string) => {
    setEditingField(fieldName)
    setMessage(null)
  }

  const handleFieldSave = async (fieldName: string, value: string) => {
    if (!user) return

    // 檢查是否有實際變更
    const originalValue = originalValues[fieldName as keyof typeof originalValues]
    if (value.trim() === originalValue.trim()) {
      setEditingField(null)
      return
    }

    setUpdateLoading(true)
    setMessage(null)

    try {
      await dispatch(updateProfile({
        [fieldName]: value.trim()
      })).unwrap()
      
      // 更新原始值
      setOriginalValues(prev => ({
        ...prev,
        [fieldName]: value.trim()
      }))
      
      setMessage({ type: 'success', text: `${fieldName === 'name' ? '姓名' : '電子郵件'}更新成功！` })
      setEditingField(null)
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '更新失敗，請稍後再試' })
      // 恢復原始值
      setFormData(prev => ({
        ...prev,
        [fieldName]: originalValue
      }))
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleFieldBlur = (fieldName: string, value: string) => {
    if (editingField === fieldName) {
      handleFieldSave(fieldName, value)
    }
  }

  const handleFieldKeyDown = (e: React.KeyboardEvent, fieldName: string, value: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleFieldSave(fieldName, value)
    } else if (e.key === 'Escape') {
      // 恢復原始值並退出編輯
      const originalValue = originalValues[fieldName as keyof typeof originalValues]
      setFormData(prev => ({
        ...prev,
        [fieldName]: originalValue
      }))
      setEditingField(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">個人設定</h1>
          <p className="text-gray-600">管理您的帳戶設定和偏好</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserCircleIcon className="w-5 h-5 inline mr-2" />
              個人資料
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'password'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <KeyIcon className="w-5 h-5 inline mr-2" />
              變更密碼
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-md ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {message.type === 'success' ? (
                    <CheckIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <XMarkIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">{message.text}</p>
                </div>
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-24 h-24 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center">
                      <UserCircleIcon className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                  <button className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700">
                    <CameraIcon className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
                  <p className="text-gray-500">{user?.email}</p>
                  <p className="text-sm text-gray-400">
                    角色: {user?.role === 'SUPER_ADMIN' ? '超級管理員' : user?.role === 'ADMIN' ? '系統管理員' : '使用者'}
                  </p>
                </div>
              </div>

              {/* Profile Form */}
              <div className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名
                  </label>
                  {editingField === 'name' ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      onBlur={(e) => handleFieldBlur('name', e.target.value)}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'name', formData.name)}
                      className="w-full px-3 py-2 border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                      disabled={updateLoading}
                    />
                  ) : (
                    <div
                      onClick={() => handleFieldClick('name')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                      title="點擊編輯姓名"
                    >
                      <span className={updateLoading && editingField === 'name' ? 'text-gray-400' : 'text-gray-900'}>
                        {formData.name || '請輸入姓名'}
                      </span>
                      {updateLoading && editingField === 'name' && (
                        <span className="ml-2 text-sm text-blue-600">儲存中...</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電子郵件
                  </label>
                  {editingField === 'email' ? (
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        onBlur={(e) => handleFieldBlur('email', e.target.value)}
                        onKeyDown={(e) => handleFieldKeyDown(e, 'email', formData.email)}
                        className="w-full pl-10 pr-3 py-2 border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                        disabled={updateLoading}
                      />
                    </div>
                  ) : (
                    <div
                      onClick={() => handleFieldClick('email')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors flex items-center"
                      title="點擊編輯電子郵件"
                    >
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className={updateLoading && editingField === 'email' ? 'text-gray-400' : 'text-gray-900'}>
                        {formData.email || '請輸入電子郵件'}
                      </span>
                      {updateLoading && editingField === 'email' && (
                        <span className="ml-2 text-sm text-blue-600">儲存中...</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Field Hints */}
                <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-md">
                  <p>💡 提示：點擊上方欄位即可直接編輯，按 Enter 儲存，按 Esc 取消</p>
                </div>
              </div>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="max-w-md">
              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    當前密碼
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    新密碼
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    minLength={6}
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">密碼至少需要 6 個字符</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    確認新密碼
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={updateLoading}
                  >
                    {updateLoading ? '更新中...' : '更新密碼'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileSettings